import {FormControl, FormField, Radio} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';
import {DiscountSectionFormType} from '../schemas/discountSectionSchema';
import {MaterialDiscountOption} from '../utils/materialDiscountOption';

const MATERIAL_OPTIONS = [
  {
    label: i18n.t('entity.order.labels.discountOnSalePrice'),
    value: MaterialDiscountOption.DiscountOnSalePrice,
  },
  {
    label: i18n.t('entity.order.labels.marginToPurchasePrice'),
    value: MaterialDiscountOption.MarginToPurchasePrice,
  },
];

interface DiscountOnMaterialProps extends RequiredTestIdProps {
  control: FormControl<DiscountSectionFormType>;
  formApi: UseFormReturn<DiscountSectionFormType>;
  isReadOnly?: boolean;
}

export function DiscountOnMaterial(props: DiscountOnMaterialProps) {
  const isExpanded = useWatch({control: props.control, name: 'isMaterialExpanded'});
  const selectedOption = useWatch({control: props.control, name: 'selectedMaterialOption'});

  return (
    <VStack spacing={4}>
      <HStack align="center" spacing={3}>
        <FormField
          control={props.control}
          name="isMaterialExpanded"
          type="switch"
          isDisabled={props.isReadOnly}
          data-testid={suffixTestId('expand-switch', props)}
        />
        <Heading size={4}>{i18n.t('entity.order.labels.discountOnMaterial')}</Heading>
      </HStack>
      <Show when={isExpanded}>
        <VStack spacing={4}>
          <Radio
            options={MATERIAL_OPTIONS}
            value={selectedOption}
            onChange={(value) =>
              props.formApi.setValue('selectedMaterialOption', value as MaterialDiscountOption, {
                shouldDirty: true,
              })
            }
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('material-options', props)}
          />
          <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
            <Show when={selectedOption === MaterialDiscountOption.DiscountOnSalePrice}>
              <FormField
                control={props.control}
                name="material.percentDiscount.discountOnSalePriceRate"
                type="number"
                label={i18n.t('entity.order.labels.percentage')}
                suffix="%"
                decimalPlaces={2}
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('discount-on-sale-price', props)}
              />
            </Show>
            <Show when={selectedOption === MaterialDiscountOption.MarginToPurchasePrice}>
              <FormField
                control={props.control}
                name="material.percentDiscount.discountMarginToPurchasePriceRate"
                type="number"
                label={i18n.t('entity.order.labels.percentage')}
                suffix="%"
                decimalPlaces={2}
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('margin-to-purchase-price', props)}
              />
            </Show>
          </Box>
        </VStack>
      </Show>
    </VStack>
  );
}
